import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAccessToken } from '@/auth/useAccessToken';
import { streamSse } from '@/lib/sseClient';

/**
 * Subscribes to the IT queue's Server-Sent Events stream and invalidates the
 * queue query whenever any ticket changes or is created — so the queue stays
 * live without manual refresh. IT-only (the stream endpoint is gated by
 * RequireIT; a non-IT caller would just get a 401/403 and the stream retries
 * harmlessly, but this hook is only mounted on the IT queue page).
 */
export function useQueueEvents(): void {
  const getAccessToken = useAccessToken();
  const queryClient = useQueryClient();

  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  useEffect(() => {
    const controller = new AbortController();

    void streamSse('/api/tickets/queue/events', {
      getAccessToken: () => getAccessTokenRef.current(),
      signal: controller.signal,
      onEvent: () => {
        void queryClient.invalidateQueries({ queryKey: ['tickets', 'queue'] });
      }
    });

    return () => controller.abort();
  }, [queryClient]);
}
